import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { sortByTimestamp, handleIndexError } from '../utils/firestoreHelpers';

// Activity Logging Service - Track all user/student activities
export const activityLogService = {
  // Log an activity
  async logActivity(userId, activityType, activityData) {
    try {
      const activityRef = collection(db, 'activityLogs');
      await addDoc(activityRef, {
        userId,
        activityType, // 'login', 'logout', 'register_session', 'submit_homework', 'complete_card', 'quiz_attempt', etc.
        activityData, // Additional data specific to the activity
        timestamp: Timestamp.now(),
        userAgent: navigator.userAgent,
        url: window.location.href
      });
    } catch (error) {
      // Don't break the app if logging fails
      console.debug('Activity logging failed (non-critical):', error);
    }
  },

  // Get activity logs for a user
  async getUserActivityLogs(userId, limitCount = 100) {
    try {
      const activityRef = collection(db, 'activityLogs');
      const q = query(
        activityRef,
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || doc.data().timestamp
      }));
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      return [];
    }
  },

  /**
   * Get all activity logs (admin only)
   * @param {number} limitCount - Maximum number of logs to return
   * @returns {Promise<Array>} Array of activity log objects
   */
  async getAllActivityLogs(limitCount = 500) {
    const activityRef = collection(db, 'activityLogs');
    try {
      const q = query(
        activityRef,
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || doc.data().timestamp
      }));
    } catch (error) {
      try {
        return await handleIndexError(
          error,
          'activityLogs',
          async () => {
            const simpleQuery = query(activityRef, limit(limitCount));
            return await getDocs(simpleQuery);
          },
          (logs) => {
            // Convert timestamps and sort
            const converted = logs.map(log => ({
              ...log,
              timestamp: log.timestamp?.toDate?.() || log.timestamp
            }));
            return sortByTimestamp(converted, 'timestamp', 'desc');
          }
        );
      } catch (fallbackError) {
        console.error('Error fetching all activity logs:', fallbackError);
        return [];
      }
    }
  },

  // Get activity logs by type
  async getActivityLogsByType(activityType, limitCount = 100) {
    try {
      const activityRef = collection(db, 'activityLogs');
      const q = query(
        activityRef,
        where('activityType', '==', activityType),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || doc.data().timestamp
      }));
    } catch (error) {
      console.error('Error fetching activity logs by type:', error);
      return [];
    }
  },

  // Activity type constants
  ACTIVITY_TYPES: {
    LOGIN: 'login',
    LOGOUT: 'logout',
    SIGNUP: 'signup',
    REGISTER_SESSION: 'register_session',
    SUBMIT_HOMEWORK: 'submit_homework',
    COMPLETE_LEARNING_CARD: 'complete_learning_card',
    QUIZ_ATTEMPT: 'quiz_attempt',
    EXERCISE_COMPLETE: 'exercise_complete',
    MATCHING_COMPLETE: 'matching_complete',
    CODE_PLAYGROUND_USE: 'code_playground_use',
    SYNTAX_GUESS_ATTEMPT: 'syntax_guess_attempt',
    VIEW_CLASSROOM: 'view_classroom',
    VIEW_SESSION: 'view_session',
    VIEW_COURSE: 'view_course',
    THEME_CHANGE: 'theme_change',
    SETTINGS_UPDATE: 'settings_update',
    DELETE_ACCOUNT: 'delete_account'
  }
};
